/**
 * The KYC document-type catalog (W8-DRIVER-ELIGIBILITY, D437, ADR-0069; owner answer D435 §5.0 items 5–6).
 *
 * A country's catalog is ONE versioned, effective-dated document maintained in the Admin under four eyes:
 *   - `types` — the document types of the country (codes are PACK DATA: this kernel never names one), each with its rules
 *     (number, expiry, front/back, photo), whether it belongs to the PERSON or to the VEHICLE, the governed advance notice
 *     before its expiry (`expiryNoticeDays`) and its display labels per language;
 *   - `requirements` — bindings that mark a type REQUIRED or OPTIONAL for a persona (driver / courier), at the country or at
 *     one of its markets, for every service category or one category. A lower scope may ADD a requirement but never drops
 *     one the country requires, unless the country row marks it `overridable`;
 *   - `vehicleRules` — per service category, the `@vello/vehicles` eligibility rule (vehicle classes, minimum model year, a
 *     valid inspection, an active vehicle) that dispatch reads through `evaluateEligibility` (the policy that had no reader).
 *
 * Pure and neutral: no country, market, document code or language literal. `validateKycCatalog` returns every problem with a
 * stable code (never the first only), so the Admin can show them all.
 */
import { sha256Hex } from '@vello/core';
export const KYC_PERSONAS = ['driver', 'courier'];
export const KYC_DOCUMENT_SUBJECTS = ['PERSON', 'VEHICLE'];
export const KYC_REQUIREMENT_LEVELS = ['REQUIRED', 'OPTIONAL'];
export const KYC_BINDING_SCOPES = ['country', 'market'];
/** A document type code: pack data, upper snake case. */
export const KYC_TYPE_CODE = /^[A-Z][A-Z0-9_]{1,39}$/;
/** A service category token (the tariff / ride category code). */
export const SERVICE_CATEGORY_TOKEN = /^[a-z][a-z0-9_]{0,31}$/;
/** A vehicle class token (`@vello/service-categories` VEHICLE_CLASSES vocabulary, validated as a token here). */
export const VEHICLE_CLASS_TOKEN = /^[a-z][a-z0-9_]{0,31}$/;
/** A label language: a bare language subtag — Country != Locale, a catalog label never carries a region. */
export const LABEL_LANGUAGE = /^[a-z]{2,3}$/;
/** An operating market id: `<cc>:<slug>`. */
export const MARKET_ID = /^[a-z]{2}:[a-z0-9][a-z0-9-]{0,62}$/;
export const KYC_MAX_NOTICE_DAYS = 365;
export const KYC_LABEL_MAX = 80;
export const KYC_MAX_TYPES = 64;
export const KYC_MAX_BINDINGS = 512;
export const KYC_VALIDITY_UNITS = ['day', 'month'];
export const KYC_MAX_VALIDITY = { day: 3650, month: 120 };
const isBool = (v) => typeof v === 'boolean';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** A non-narrowing object check (keeps the element's declared type for the checks that follow). */
const isRec = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** True when `marketId` is a market of `country` (`br:…` belongs to `BR`). */
export function marketBelongsToCountry(marketId, country) {
    return MARKET_ID.test(marketId) && marketId.slice(0, 2) === country.toLowerCase();
}
function bindingKey(b) {
    return [b.documentType, b.persona, b.scope, b.marketId ?? '', b.category ?? ''].join('|');
}
function validRule(rule) {
    if (!isObj(rule))
        return false;
    const allowed = new Set(['allowedVehicleTypes', 'minVehicleYear', 'minRating', 'minCompletedTrips', 'requireValidInspection', 'requireApprovedDriver', 'requireActiveVehicle']);
    for (const k of Object.keys(rule))
        if (!allowed.has(k))
            return false;
    const types = rule.allowedVehicleTypes;
    if (types !== undefined && (!Array.isArray(types) || types.length === 0 || !types.every((t) => typeof t === 'string' && VEHICLE_CLASS_TOKEN.test(t)) || new Set(types).size !== types.length))
        return false;
    const year = rule.minVehicleYear;
    if (year !== undefined && (typeof year !== 'number' || !Number.isInteger(year) || year < 1900 || year > 2100))
        return false;
    const rating = rule.minRating;
    if (rating !== undefined && (typeof rating !== 'number' || !Number.isFinite(rating) || rating < 0 || rating > 5))
        return false;
    const trips = rule.minCompletedTrips;
    if (trips !== undefined && (typeof trips !== 'number' || !Number.isInteger(trips) || trips < 0))
        return false;
    for (const flag of ['requireValidInspection', 'requireApprovedDriver', 'requireActiveVehicle']) {
        if (rule[flag] !== undefined && !isBool(rule[flag]))
            return false;
    }
    return true;
}
/**
 * Every problem of a catalog, each with a stable code and a path. An empty list = the catalog may be proposed. The same
 * check runs at proposal AND at approval (a catalog is never approved on a stale validation).
 */
export function validateKycCatalog(catalog) {
    const problems = [];
    const push = (code, path) => void problems.push({ code, path });
    if (!isObj(catalog) || typeof catalog.country !== 'string' || !/^[A-Z]{2}$/.test(catalog.country)) {
        return [{ code: 'CATALOG_COUNTRY_INVALID', path: 'country' }];
    }
    const types = Array.isArray(catalog.types) ? catalog.types : [];
    const requirements = Array.isArray(catalog.requirements) ? catalog.requirements : [];
    const vehicleRules = Array.isArray(catalog.vehicleRules) ? catalog.vehicleRules : [];
    if (types.length > KYC_MAX_TYPES || requirements.length > KYC_MAX_BINDINGS || vehicleRules.length > KYC_MAX_TYPES)
        push('CATALOG_TOO_LARGE', '');
    const byCode = new Map();
    types.forEach((t, i) => {
        const path = `types[${i}]`;
        if (!isRec(t) || typeof t.code !== 'string' || !KYC_TYPE_CODE.test(t.code)) {
            push('TYPE_CODE_INVALID', path);
            return;
        }
        if (byCode.has(t.code))
            push('TYPE_DUPLICATE', path);
        else
            byCode.set(t.code, t);
        if (!KYC_DOCUMENT_SUBJECTS.includes(t.subject))
            push('TYPE_SUBJECT_INVALID', path);
        const r = t.rules;
        if (!isObj(r) || !isBool(r.number) || !isBool(r.expiry) || !isBool(r.frontBack) || !isBool(r.photo) || Object.keys(r).length !== 4)
            push('TYPE_RULES_INVALID', path);
        if (!isBool(t.active))
            push('TYPE_RULES_INVALID', path);
        const labels = isObj(t.labels) ? Object.entries(t.labels) : [];
        if (labels.length === 0)
            push('TYPE_LABEL_MISSING', path);
        else if (labels.some(([lang, text]) => !LABEL_LANGUAGE.test(lang) || typeof text !== 'string' || text.trim() === '' || text.length > KYC_LABEL_MAX))
            push('TYPE_LABEL_INVALID', path);
        if (t.expiryNoticeDays !== undefined) {
            if (typeof t.expiryNoticeDays !== 'number' || !Number.isInteger(t.expiryNoticeDays) || t.expiryNoticeDays < 1 || t.expiryNoticeDays > KYC_MAX_NOTICE_DAYS)
                push('TYPE_NOTICE_DAYS_INVALID', path);
            else if (isObj(r) && r.expiry !== true)
                push('TYPE_NOTICE_WITHOUT_EXPIRY', path);
        }
        if (t.validityFromIssue !== undefined) {
            const v = t.validityFromIssue;
            const valid = isObj(v) && Object.keys(v).length === 2 && KYC_VALIDITY_UNITS.includes(v.unit)
                && typeof v.amount === 'number' && Number.isInteger(v.amount) && v.amount >= 1 && v.amount <= KYC_MAX_VALIDITY[v.unit];
            if (!valid)
                push('TYPE_VALIDITY_FROM_ISSUE_INVALID', path);
            else if (isObj(r) && r.expiry !== true)
                push('TYPE_VALIDITY_WITHOUT_EXPIRY', path);
        }
    });
    const seen = new Set();
    requirements.forEach((b, i) => {
        const path = `requirements[${i}]`;
        if (!isRec(b)) {
            push('BINDING_UNKNOWN_TYPE', path);
            return;
        }
        const type = byCode.get(b.documentType);
        if (type === undefined)
            push('BINDING_UNKNOWN_TYPE', path);
        else if (!type.active && b.requirement === 'REQUIRED')
            push('BINDING_RETIRED_TYPE', path);
        if (!KYC_PERSONAS.includes(b.persona))
            push('BINDING_PERSONA_INVALID', path);
        if (!KYC_REQUIREMENT_LEVELS.includes(b.requirement))
            push('BINDING_LEVEL_INVALID', path);
        if (b.scope === 'country') {
            if (b.marketId !== undefined)
                push('BINDING_SCOPE_INVALID', path);
            if (b.overridable !== undefined && !isBool(b.overridable))
                push('BINDING_SCOPE_INVALID', path);
        }
        else if (b.scope === 'market') {
            if (typeof b.marketId !== 'string' || !MARKET_ID.test(b.marketId))
                push('BINDING_MARKET_INVALID', path);
            else if (!marketBelongsToCountry(b.marketId, catalog.country))
                push('BINDING_MARKET_OUTSIDE_COUNTRY', path);
            if (b.overridable !== undefined)
                push('BINDING_OVERRIDABLE_BELOW_COUNTRY', path);
        }
        else {
            push('BINDING_SCOPE_INVALID', path);
        }
        // A country+category row may carry `overridable` too (it then governs that category's markets).
        if (b.category !== undefined && (typeof b.category !== 'string' || !SERVICE_CATEGORY_TOKEN.test(b.category)))
            push('BINDING_CATEGORY_INVALID', path);
        const key = bindingKey(b);
        if (seen.has(key))
            push('BINDING_DUPLICATE', path);
        seen.add(key);
    });
    // No lower scope drops a country requirement that is not overridable (AGENTS.md: a lower scope never loosens a
    // country-level legal/safety constraint unless the setting explicitly permits it).
    requirements.forEach((b, i) => {
        if (!isRec(b) || b.requirement !== 'OPTIONAL')
            return;
        // `c` locks `b` when both reach a common category (either names none, or the same one) and `b` sits below `c`: at a
        // market, or at the country for one category under a country row for every category.
        const locks = requirements.filter((c) => isRec(c) && c !== b && c.scope === 'country' && c.requirement === 'REQUIRED' && c.overridable !== true
            && c.documentType === b.documentType && c.persona === b.persona
            && (c.category === undefined || b.category === undefined || c.category === b.category)
            && (b.scope === 'market' || (c.category === undefined && b.category !== undefined)));
        if (locks.length > 0)
            push('LOWER_SCOPE_DROPS_COUNTRY_REQUIREMENT', `requirements[${i}]`);
    });
    const ruleCategories = new Set();
    vehicleRules.forEach((v, i) => {
        const path = `vehicleRules[${i}]`;
        if (!isRec(v) || typeof v.category !== 'string' || !SERVICE_CATEGORY_TOKEN.test(v.category)) {
            push('VEHICLE_RULE_CATEGORY_INVALID', path);
            return;
        }
        if (ruleCategories.has(v.category))
            push('VEHICLE_RULE_DUPLICATE', path);
        ruleCategories.add(v.category);
        if (!validRule(v.rule))
            push('VEHICLE_RULE_INVALID', path);
    });
    // Owner answer (PR 291 question 5): one rule per vehicle class, two booleans, a class token.
    const classRules = Array.isArray(catalog.vehicleClassRules) ? catalog.vehicleClassRules : [];
    if (catalog.vehicleClassRules !== undefined && !Array.isArray(catalog.vehicleClassRules))
        push('VEHICLE_CLASS_RULE_INVALID', 'vehicleClassRules');
    if (classRules.length > KYC_MAX_TYPES)
        push('CATALOG_TOO_LARGE', 'vehicleClassRules');
    const ruleClasses = new Set();
    classRules.forEach((v, i) => {
        const path = `vehicleClassRules[${i}]`;
        if (!isObj(v) || Object.keys(v).length !== 3 || typeof v.vehicleClass !== 'string' || !VEHICLE_CLASS_TOKEN.test(v.vehicleClass)
            || !isBool(v.requiresInspection) || !isBool(v.requiresVehicleDocuments)) {
            push('VEHICLE_CLASS_RULE_INVALID', path);
            return;
        }
        if (ruleClasses.has(v.vehicleClass))
            push('VEHICLE_CLASS_RULE_DUPLICATE', path);
        ruleClasses.add(v.vehicleClass);
    });
    return problems;
}
/** The catalog's rule for a vehicle class (courier persona), or undefined (= inspection and documents required). */
export function vehicleClassRule(catalog, vehicleClass) {
    if (vehicleClass === undefined)
        return undefined;
    return catalog.vehicleClassRules?.find((r) => r.vehicleClass === vehicleClass);
}
/** The canonical serialization the content hash covers (sorted keys, a type tag per value, absent = null). */
export function canonicalKycValue(value) {
    if (value === null || value === undefined)
        return 'z';
    const t = typeof value;
    if (t === 'string')
        return `s:${JSON.stringify(value)}`;
    if (t === 'boolean')
        return `b:${value ? '1' : '0'}`;
    if (t === 'number') {
        if (!Number.isFinite(value))
            throw new TypeError('a catalog value has a non-finite number');
        return `n:${JSON.stringify(value)}`;
    }
    if (Array.isArray(value))
        return `a:[${value.map(canonicalKycValue).join(',')}]`;
    if (t === 'object') {
        const rec = value;
        return `o:{${Object.keys(rec).filter((k) => rec[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canonicalKycValue(rec[k])}`).join(',')}}`;
    }
    throw new TypeError(`a catalog value has an unsupported type: ${t}`);
}
/** The content hash of a catalog: two catalogs with the same content hash decide the same (key order never matters). */
export function hashKycCatalog(catalog) {
    return sha256Hex(canonicalKycValue(catalog));
}
/** The stable reference a decision carries: `kyc-catalog:<CC>#v<version>`. */
export function kycCatalogRef(country, version) {
    return `kyc-catalog:${country}#v${version}`;
}
/** The version in force at `at`: the highest approved version whose `effectiveFrom` is not after `at`. */
export function effectiveCatalogVersion(versions, at) {
    const atMs = Date.parse(at);
    let best;
    for (const v of versions) {
        if (Date.parse(v.effectiveFrom) > atMs)
            continue;
        if (best === undefined || v.version > best.version)
            best = v;
    }
    return best;
}
/** A catalog type by code, or undefined. */
export function catalogType(catalog, code) {
    return catalog.types.find((t) => t.code === code);
}
/** The label of a type in `lang` (bare subtag), else English, else the first label, else the code. */
export function kycTypeLabel(type, lang) {
    const base = lang.slice(0, 2).toLowerCase();
    return type.labels[base] ?? type.labels.en ?? Object.values(type.labels)[0] ?? type.code;
}
//# sourceMappingURL=catalog.js.map